<script setup lang="ts">
import { computed, onUnmounted, ref, toRefs, watch, onMounted } from 'vue';
import { useCustomerMgmt } from '@ilteducation/customer-mgmt';
import type { FetchListOptions, PaginationOptions } from '@ilteducation/customer-mgmt';
import { useDisplay } from 'vuetify';
import { VDataTableServer } from 'vuetify/components';
import { VTextField } from 'vuetify/components/VTextField';
import useLocale from './helpers/use-locale';

type ReadonlyHeaders = Partial<VDataTableServer['headers']> & { key: string }[];

type Emits = {
  (e: 'update:modelValue', selectedUsers: string[]): void;
};
const emit = defineEmits<Emits>();

type Props = {
  modelValue: string[]; // selected
  schoolUnitId: string;
  userId?: string;
  userType?: 'employee' | 'student' | 'guardian';
  isFetchingSchoolUnits?: boolean;
  forceSmaller?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  userId: undefined,
  userType: undefined,
  isFetchingSchoolUnits: false,
  forceSmaller: undefined,
});

const { t } = useLocale();
const { smAndDown, mdAndDown, md } = useDisplay();

const defaultOptionsUsers = {
  page: 1,
  itemsPerPage: 10,
  sortBy: ['name'],
  sortDesc: [false],
  groupBy: [],
  groupDesc: [false],
  mustSort: false,
  multiSort: false,
  searchString: '',
  filters: {
    ...(props.userType ? { type: props.userType } : {}),
  },
  force: false,
} as FetchListOptions;

const { userType, forceSmaller, schoolUnitId, isFetchingSchoolUnits } = toRefs(props);
const optionsUsers = ref(defaultOptionsUsers);
const searchString = ref<string>('');
const searchInputRef = ref<VTextField>();

const isSmAndDown = computed(() => forceSmaller.value ?? smAndDown.value);
const isMdAndDown = computed(() => forceSmaller.value ?? mdAndDown.value);

const {
  displayedItems: users,
  isFetching: isFetchingUsers,
  totalItems: totalUsers,
  setSearchField: setSearchFieldUsers,
  setFetchParams: setFetchParamsUsers,
  cleanAndFetch: cleanAndFetchUsers,
  resetToDefaults: resetToDefaultUsers,
} = useCustomerMgmt({ context: 'users', options: defaultOptionsUsers });

const usersList = computed(() => users.value.filter((user) => user.uid !== props.userId));

const searchUsers = () => {
  if (searchInputRef.value) {
    searchInputRef.value.blur();
  }

  if (userType?.value) {
    optionsUsers.value.filters = { ...optionsUsers.value.filters, ...{ type: userType?.value } };
  }
  optionsUsers.value.searchString = searchString.value;
  setFetchParamsUsers({ ...optionsUsers.value });
  setSearchFieldUsers(searchString.value); // might not be needed. check if it is
  cleanAndFetchUsers();
};

const loadUsersOnPageChange = (page: PaginationOptions['page']) => {
  optionsUsers.value.page = page;
  setFetchParamsUsers({ ...optionsUsers.value });
  if (schoolUnitId.value) {
    cleanAndFetchUsers();
  }
};
const loadUsersOnItemsPerPageChange = (itemsPerPage: PaginationOptions['itemsPerPage']) => {
  optionsUsers.value.itemsPerPage = itemsPerPage;
  setFetchParamsUsers({ ...optionsUsers.value });
  if (schoolUnitId.value) {
    cleanAndFetchUsers();
  }
};
const loadUsersOnSortByChange = (sortBy: PaginationOptions['sortBy']) => {
  if (sortBy.length > 0) {
    optionsUsers.value.sortBy = [sortBy[0].key];
    optionsUsers.value.sortDesc = [sortBy[0].order !== 'asc'];
  }
  setFetchParamsUsers({ ...optionsUsers.value });
  if (schoolUnitId.value) {
    cleanAndFetchUsers();
  }
};

watch(schoolUnitId, async () => {
  optionsUsers.value.filters = { ...optionsUsers.value.filters, ...{ schoolUnits: [schoolUnitId.value] } };

  resetToDefaultUsers();
  setFetchParamsUsers(optionsUsers.value);
  if (!isFetchingUsers.value) {
    await cleanAndFetchUsers();
  }
});

const updateModel = (selectedUsers: string[]) => {
  emit('update:modelValue', selectedUsers);
};

const doSelectAll = (allSelected: boolean, defaultSelectAll: (allSelected: boolean) => void) => {
  const maxSelectedItems = 120;
  if (allSelected) {
    updateModel([]);
    return;
  }

  if (props.modelValue.length > maxSelectedItems) {
    const selected = props.modelValue.slice(0, maxSelectedItems).map((u) => u);
    updateModel(selected);
    return;
  }
  defaultSelectAll(!allSelected);
};

const doToggleSort = (column: any, callback: (column: any) => void) => {
  if (!column?.sortable) {
    return;
  }
  callback(column);
};

onUnmounted(() => {
  optionsUsers.value = defaultOptionsUsers;
  resetToDefaultUsers();
  setFetchParamsUsers(optionsUsers.value);
});

onMounted(async () => {
  optionsUsers.value.filters = { ...optionsUsers.value.filters, ...{ schoolUnits: [schoolUnitId.value] } };
  resetToDefaultUsers();
  setFetchParamsUsers(optionsUsers.value);
  if (!isFetchingUsers.value) {
    await cleanAndFetchUsers();
  }
});

const tableHeaders = computed(() =>
  [
    { title: 'components.name', key: 'name', value: 'name', hide: false },
    { title: 'components.user_name', key: 'userName', value: 'userName', width: '20%', hide: isMdAndDown.value },
    {
      title: 'components.role',
      key: 'roles',
      value: 'roles',
      width: '20%',
      hide: isSmAndDown.value,
      sortable: false,
    },
    {
      title: 'components.groups',
      key: 'groups',
      value: 'groups',
      width: '20%',
      hide: isSmAndDown.value,
      sortable: false,
    },
    {
      key: 'data-table-select',
      hide: false,
      align: 'center',
      width: md.value ? '120px' : '150px',
    },
  ].filter((header) => !header.hide),
);
</script>

<template>
  <div>
    <v-text-field
      id="search-input"
      ref="searchInputRef"
      v-model="searchString"
      type="search"
      class="mx-4"
      :label="t('components.input_search')"
      :aria-label="t('components.input_search')"
      prepend-inner-icon="mdi-magnify"
      :single-line="true"
      hide-details
      variant="solo-filled"
      :clearable="true"
      density="comfortable"
      rounded="lg"
      flat
      @keyup.enter="searchUsers"
      @click:clear="searchUsers"
    />
    <v-card-text>
      <v-data-table-server
        :model-value="modelValue"
        :headers="tableHeaders as ReadonlyHeaders"
        :items="usersList"
        :loading="isFetchingUsers || isFetchingSchoolUnits"
        :search="optionsUsers.searchString"
        :items-per-page="optionsUsers.itemsPerPage"
        :items-length="totalUsers ?? 0"
        :multi-sort="false"
        :loading-text="t('components.loading_users')"
        :no-data-text="t('components.no_data_available')"
        :items-per-page-text="t('components.items_per_page')"
        :next-page-label="t('components.next_page')"
        :page-text="`{0} - {1} ${t('components.of')} {2}`"
        :prev-page-label="t('components.prev_page')"
        :last-page-label="t('components.last_page')"
        :items-per-page-options="[
          { value: 10, title: '10' },
          { value: 15, title: '15' },
          { value: 30, title: '30' },
        ]"
        item-key="uid"
        item-value="uid"
        class="data-table"
        @update:page="loadUsersOnPageChange"
        @update:itemsPerPage="loadUsersOnItemsPerPageChange"
        @update:sortBy="loadUsersOnSortByChange"
        @update:modelValue="updateModel"
      >
        <template #[`headers`]="{ columns, isSorted, getSortIcon, toggleSort, selectAll, allSelected }">
          <tr>
            <template v-for="column in columns" :key="column.key">
              <th
                v-if="column.key === 'data-table-select'"
                class="bg-transparent mr-2"
                scope="col"
                :class="`text-${column.align}`"
                :style="`min-width: ${column.width}`"
              >
                <v-btn
                  class="pa-0 px-1"
                  :disabled="isFetchingUsers"
                  variant="text"
                  @click="doSelectAll(allSelected, selectAll)"
                >
                  <span class="text-none select-all">
                    {{ allSelected ? t('components.deselect_all') : t('components.select_all') }}
                  </span>
                </v-btn>
              </th>
              <th
                v-else
                class="bg-transparent mr-2"
                scope="col"
                :class="{
                  'cursor-pointer': column.sortable,
                  'text-center': column.align === 'center',
                  'text-start': column.align === 'start',
                }"
                :style="`width: ${column.width}`"
                @click="doToggleSort(column, toggleSort)"
              >
                {{ t(column.title ?? '') }}
                <template v-if="column.sortable && isSorted(column)">
                  <v-icon :icon="getSortIcon(column)"></v-icon>
                </template>
              </th>
            </template>
          </tr>
        </template>
        <template #[`item.name`]="{ value }">
          <div class="overflow-hidden" :style="{ width: md ? '130px' : 'auto' }">
            <span>{{ value }}</span>
          </div>
        </template>
        <template #[`item.userName`]="{ value }">
          <div class="overflow-hidden" :style="{ width: '200px' }">
            <div class="ellipsis">{{ value }}</div>
          </div>
        </template>
        <template #[`item.roles`]="{ item }">
          <span class="text-truncate">
            <div class="d-flex overflow-hidden" :style="{ width: md ? '80px' : '100px' }">
              <div class="ellipsis">
                {{ item.roles.map((role: string) => t(`components.roles.${role}`)).join(', ') }}
              </div>
            </div>
          </span>
        </template>
        <template #[`item.groups`]="{ item }">
          <span class="text-truncate">
            <div class="d-flex overflow-hidden" :style="{ width: md ? '80px' : '150px' }">
              <div class="ellipsis">{{ item.groups.join(', ') }}</div>
            </div>
          </span>
        </template>
        <template #[`item.data-table-select`]="{ item, internalItem, isSelected, toggleSelect }">
          <v-checkbox-btn
            :model-value="isSelected(internalItem)"
            :aria-label="t('components.select_item', { name: item.name })"
            @update:model-value="toggleSelect(internalItem)"
          ></v-checkbox-btn>
        </template>
      </v-data-table-server>
    </v-card-text>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.data-table {
  border-radius: 10px;
  height: 100%;
}

.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
:deep(.v-table__wrapper) {
  overflow: visible;
}
:deep(.v-selection-control) {
  justify-content: center;
}

:deep(input) {
  outline: none !important;
}
.select-all {
  text-wrap: wrap;
}
@media #{map.get(v.$display-breakpoints, 'xs')} {
  :deep(.v-data-table__td) {
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 70px;
    overflow: hidden;
  }
}
</style>
